// Google Identity Services(GIS)経由の OAuth 2.0 暗黙フロー。
// Phase 1 は refresh_token を使わず、access_token の期限切れ → 再ログインで対応。
//
// 必須環境変数:
// - VITE_GOOGLE_CLIENT_ID: Google Cloud Console で発行した OAuth 2.0 Client ID
//
// 必須スコープ:
// - https://www.googleapis.com/auth/drive.file
//   (つばめカードが作成・選択したファイルだけ読み書き可)

import { SHEETS_SCOPE } from "./constants";

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (resp: {
              access_token: string;
              expires_in: number;
              error?: string;
            }) => void;
          }): { requestAccessToken(): void };
        };
      };
    };
  }
}

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let gisLoaded = false;

export async function loadGisScript(): Promise<void> {
  if (gisLoaded) return;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google Identity Services の読み込みに失敗しました"));
    document.head.appendChild(script);
  });
  gisLoaded = true;
}

export interface AcquiredToken {
  accessToken: string;
  expiresAt: number; // epoch milliseconds
}

export async function requestAccessToken(): Promise<AcquiredToken> {
  await loadGisScript();

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error("VITE_GOOGLE_CLIENT_ID が設定されていません");
  }

  return new Promise<AcquiredToken>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SHEETS_SCOPE,
      callback: (resp) => {
        if (resp.error) {
          reject(new Error(`OAuth エラー: ${resp.error}`));
          return;
        }
        const expiresAt = Date.now() + resp.expires_in * 1000;
        resolve({ accessToken: resp.access_token, expiresAt });
      },
    });
    client.requestAccessToken();
  });
}
